/**
 * AppConfig.js
 * Configuración centralizada de la aplicación siguiendo patrón Singleton
 * para garantizar consistencia a través de todos los módulos.
 * @version 1.1.0 - Soporte para subdominios especializados
 */

class AppConfig {
  constructor() {
    // Capturar configuración de Firebase Hosting si existe
    this.firebaseConfig = typeof window !== 'undefined' && 
      window.__FIREBASE_CONFIG__ ? 
      window.__FIREBASE_CONFIG__ : null;
    
    // Detección dinámica de entorno con extrema precisión
    const isProduction = process.env.NODE_ENV === 'production';
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isFirebaseHosting = hostname === 'nubelytics.web.app' || 
                            hostname === 'api-nubelytics.web.app' ||
                            hostname === 'sync-nubelytics.web.app';
    const isCustomDomain = hostname === 'nubelytics.com' || 
                          hostname === 'api.nubelytics.com' || 
                          hostname === 'sync.nubelytics.com';
    
    // Detección específica de subdominios
    const isApiSubdomain = hostname === 'api.nubelytics.com' || hostname === 'api-nubelytics.web.app';
    const isSyncSubdomain = hostname === 'sync.nubelytics.com' || hostname === 'sync-nubelytics.web.app';
    
    // Configuración inteligente de URLs para API absolutas
    // ¡IMPORTANTE! Todas las URLs son absolutas para evitar problemas de CORS y HTML inesperado
    let detectedApiBase;
    
    if (process.env.REACT_APP_API_BASE_URL) {
      // Prioridad 1: Variable de entorno explícita
      detectedApiBase = process.env.REACT_APP_API_BASE_URL;
    } else if (isApiSubdomain) {
      // Ya estamos en api.nubelytics.com → usar mismo origen
      detectedApiBase = window.location.origin;
    } else if (isFirebaseHosting || (isCustomDomain && !isApiSubdomain && !isSyncSubdomain)) {
      // Dominio raíz (nubelytics.com) o Firebase hosting raíz → usar mismo origen con prefijo /api
      detectedApiBase = `${window.location.origin}/api`;
    } else {
      // Fallback genérico a subdominio dedicado
      detectedApiBase = 'https://api.nubelytics.com';
    }
    
    this.apiBaseUrl = this._normalizeApiUrl(detectedApiBase);
    
    // Configuración del microservicio de sincronización
    let detectedSyncBase;
    if (process.env.REACT_APP_SYNC_BASE_URL) {
      detectedSyncBase = process.env.REACT_APP_SYNC_BASE_URL;
    } else if (isSyncSubdomain) {
      detectedSyncBase = window.location.origin;
    } else if (isFirebaseHosting || (isCustomDomain && !isApiSubdomain && !isSyncSubdomain)) {
      detectedSyncBase = `${window.location.origin}/api/sync`;
    } else {
      detectedSyncBase = 'https://sync.nubelytics.com';
    }
    
    this.syncBaseUrl = this._normalizeApiUrl(detectedSyncBase);
    
    this.frontendDomain = hostname || 'nubelytics.com';
    
    // Debug info - esto ayudará a diagnosticar problemas de dominio
    this.currentDomain = hostname;
    this.isFirebaseHosting = isFirebaseHosting;
    this.isCustomDomain = isCustomDomain;
    this.isApiSubdomain = isApiSubdomain;
    this.isSyncSubdomain = isSyncSubdomain;
    
    // Cliente OAuth
    this.apiClientId = process.env.REACT_APP_API_CLIENT_ID;
    this.apiClientSecret = process.env.REACT_APP_API_CLIENT_SECRET;
    
    // Base URL para autenticación OAuth2
    // CORRECCIÓN CRÍTICA: Siempre usamos https://api.nubelytics.com como base para auth
    // para evitar recibir HTML en lugar de JSON desde endpoints incorrectos
    this.authBaseUrl = this._normalizeApiUrl(
      process.env.REACT_APP_AUTH_BASE_URL || 
      'https://api.nubelytics.com' // Forzar endpoint correcto en lugar de this.apiBaseUrl
    );
    
    // Otros parámetros de configuración
    this.debug = process.env.REACT_APP_DEBUG === 'true';
    
    // Log de diagnóstico en modo debug
    if (this.debug) {
      console.log('[AppConfig] Configuración inicializada:', {
        apiBaseUrl: this.apiBaseUrl,
        syncBaseUrl: this.syncBaseUrl,
        authBaseUrl: this.authBaseUrl,
        hasFirebaseConfig: !!this.firebaseConfig,
      });
      
      // Guardar valores originales para diagnóstico sin exponer secrets
      localStorage.setItem('nubelytics_api_base_url', this.apiBaseUrl);
      localStorage.setItem('nubelytics_sync_base_url', this.syncBaseUrl);
      localStorage.setItem('nubelytics_app_domain', window.location.hostname);
    }
  }

  /**
   * Normaliza una URL de API para asegurar formato consistente
   * @param {string} url - URL a normalizar
   * @return {string} URL normalizada
   */
  _normalizeApiUrl(url) {
    if (!url) return '';
    
    // Quitar barra final si existe
    url = url.replace(/\/+$/, '');
    
    // IMPORTANTE: NO eliminar el segmento /api en dominios personalizados
    // En dominios personalizados, la API está en /api (ej. nubelytics.com/api)
    // mientras que en dominios específicos de API (api.nubelytics.com) no lleva /api
    
    // Registrar para diagnóstico
    if (this.debug) {
      try {
        localStorage.setItem('nubelytics_api_url_original', url);
      } catch (e) {/* Error no crítico */}
    }
    
    return url;
  }
  
  /**
   * Obtiene la URL completa para endpoints de API
   * @param {string} path - Ruta relativa del endpoint 
   * @return {string} URL completa
   */
  getApiUrl(path) {
    if (!path) return this.apiBaseUrl;
    
    // Normalizar path (asegurar que comience con /)
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // CORRECCIÓN CRÍTICA: No usamos getApiUrl para endpoints de auth
    // Los endpoints de auth deben usar exclusivamente getAuthUrl
    if (normalizedPath.startsWith('/auth/')) {
      console.warn('[AppConfig] Usando getApiUrl con endpoint auth:', normalizedPath);
      return this.getAuthUrl(normalizedPath.substring(5)); // Remover /auth
    }
    
    // Determinar si apiBaseUrl ya contiene el segmento /api
    const baseContainsApiSegment = this.apiBaseUrl.includes('/api');
    
    // Componer URL completa evitando duplicar el segmento /api
    const url = baseContainsApiSegment ? 
      `${this.apiBaseUrl}${normalizedPath}` : 
      `${this.apiBaseUrl}/api${normalizedPath}`;
    
    // Guardar diagnóstico completo para depurar problemas de URL
    if (this.debug) {
      try {
        localStorage.setItem('nubelytics_get_api_url_original_path', normalizedPath);
        localStorage.setItem('nubelytics_get_api_url_result', url);
        localStorage.setItem('nubelytics_base_contains_api', String(baseContainsApiSegment));
      } catch (e) {/* Error no crítico */}
    }
    
    return url;
  }
  
  /**
   * Obtiene la URL completa para endpoints de autenticación
   * @param {string} path - Ruta relativa del endpoint 
   * @return {string} URL completa
   */
  /**
   * Obtiene la URL completa para endpoints del microservicio de sincronización
   * @param {string} path - Ruta relativa del endpoint 
   * @return {string} URL completa del microservicio sync
   */
  getSyncUrl(path) {
    if (!path) return this.syncBaseUrl;
    
    // Normalizar path (asegurar que comience con /)
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Determinar si syncBaseUrl ya contiene el segmento /sync
    const baseContainsSyncSegment = this.syncBaseUrl.includes('/sync');
    
    // En dominios específicos no añadir /sync, en dominios generales sí
    const url = this.isSyncSubdomain || baseContainsSyncSegment ? 
      `${this.syncBaseUrl}${normalizedPath}` : 
      `${this.syncBaseUrl}/sync${normalizedPath}`;
    
    // Guardar diagnóstico completo para depurar problemas de URL
    if (this.debug) {
      try {
        localStorage.setItem('nubelytics_get_sync_url_original_path', normalizedPath);
        localStorage.setItem('nubelytics_get_sync_url_result', url);
        localStorage.setItem('nubelytics_sync_base_contains_segment', String(baseContainsSyncSegment));
      } catch (e) {/* Error no crítico */}
    }
    
    return url;
  }
  
  getAuthUrl(path) {
    if (!path) return this.authBaseUrl;
    
    // Normalizar path (asegurar que comience con /)
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Si el path ya contiene el segmento /convert-token u /oauth/, estamos en OAuth2
    const isOAuth2Endpoint = normalizedPath.includes('/convert-token') || 
                            normalizedPath.includes('/token') ||
                            normalizedPath.includes('/oauth/');
    
    // Determinar URLs base y segmentos basados en el tipo de endpoint
    let baseUrl = this.authBaseUrl;
    let pathPrefix = '/auth';
    
    // Componer URL correctamente según RFC 6749 para OAuth2
    // Evitar prefijos /api en endpoints de autenticación
    if (baseUrl.includes('/api') && isOAuth2Endpoint) {
      // Reemplazar /api por /auth para endpoints OAuth2
      baseUrl = baseUrl.replace(/\/api(\/?$|\/)/g, '/auth/');
    }
    
    // Verificar si la baseUrl ya tiene el segmento /auth
    const baseContainsAuthSegment = baseUrl.includes('/auth');
    
    // Componer URL final evitando duplicación de segmentos
    let url;
    if (baseContainsAuthSegment) {
      // Si ya tiene /auth, añadir el path normalizado sin duplicar /auth
      url = normalizedPath.startsWith('/auth/') ?
        `${baseUrl}${normalizedPath.substring(5)}` : // Remover /auth
        `${baseUrl}${normalizedPath}`;
    } else {
      // Si no tiene /auth, añadirlo
      url = `${baseUrl}${pathPrefix}${normalizedPath}`;
    }
    
    // Garantizar que no haya doble barra
    url = url.replace(/\/\//g, '/');
    // Recuperar el protocolo que podría haberse perdido
    url = url.replace(/^(https?:)\//, '$1//');
    
    // Registrar para diagnóstico detallado
    if (this.debug) {
      try {
        localStorage.setItem('nubelytics_get_auth_url_path', normalizedPath);
        localStorage.setItem('nubelytics_get_auth_url_base', baseUrl);
        localStorage.setItem('nubelytics_get_auth_url_result', url);
        localStorage.setItem('nubelytics_base_contains_auth', String(baseContainsAuthSegment));
        localStorage.setItem('nubelytics_is_oauth2_endpoint', String(isOAuth2Endpoint));
      } catch (e) {/* Error no crítico */}
    }
    
    return url;
  }
  
  /**
   * Detecta si la aplicación está ejecutándose en producción
   * @return {boolean}
   */
  isProduction() {
    return process.env.NODE_ENV === 'production';
  }
  
  /**
   * Detecta si la aplicación está ejecutándose en Firebase Hosting
   * @return {boolean}
   */
  isFirebaseHosting() {
    return typeof window !== 'undefined' && !!window.__FIREBASE_CONFIG__;
  }
}

// Exportar singleton
const appConfig = new AppConfig();
export default appConfig;
